// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { loginWithCredentials, validateToken } from '../services/userAPIService';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);

  const checkTokenValidity = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsAuthenticated(false);
      setHasSubscription(false);
      setIsLoading(false);
      return;
    }

    try {
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Token validation timeout')), 8000);
      });

      // Race between the actual validation and the timeout
      const response = await Promise.race([
        validateToken(),
        timeoutPromise
      ]);

      setIsAuthenticated(true);
      setHasSubscription(response.has_subscription);
    } catch (error) {
      console.error('Token validation failed:', error.message);
      // If it's a timeout error, we might want to keep the user logged in
      if (error.message === 'Token validation timeout') {
        // Keep the current authentication state
        console.log('Token validation timed out, keeping current session');
      } else {
        // For other errors, log the user out
        setIsAuthenticated(false);
        setHasSubscription(false);
        localStorage.removeItem('token');
      }
    }
    setIsLoading(false);
  }, []);

  // Initial auth check
  useEffect(() => {
    const initAuth = async () => {
      // Add a small delay to prevent unnecessary cold starts for quick navigations
      const delay = new Promise(resolve => setTimeout(resolve, 100));
      await Promise.all([checkTokenValidity(), delay]);
    };

    initAuth();
  }, [checkTokenValidity]);

  const login = async (credentials) => {
    try {
      const response = await loginWithCredentials(credentials);
      localStorage.setItem('token', response.access_token);
      setIsAuthenticated(true);
      // After login, check subscription status
      await checkTokenValidity();
      return { status: 200 };
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setHasSubscription(false);
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isLoading, 
      login, 
      logout,
      hasSubscription,
      refreshSubscriptionStatus: checkTokenValidity 
    }}>
      {children}
    </AuthContext.Provider>
  );
};